import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon } from '../icons';

// Sovereign Emerald palette — mirrors sidebar / header tokens
const SE = {
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneRing: 'rgba(201,169,97,0.40)',
  hairline:      'rgba(11,59,46,0.12)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
  hoverWash:     'rgba(11,59,46,0.05)',
};

interface BreadcrumbItem {
  name: string;
  path?: string;
}

interface PageBreadcrumbProps {
  pageTitle?: string;
  items?: BreadcrumbItem[];
  breadcrumbs?: BreadcrumbItem[];
}

const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  pageTitle,
  items,
  breadcrumbs: breadcrumbsProp,
}) => {
  const location = useLocation();

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ name: 'Home', path: '/' }];
    let currentPath = '';
    segments.forEach((seg, idx) => {
      currentPath += `/${seg}`;
      crumbs.push({
        name: seg.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        path: idx === segments.length - 1 ? undefined : currentPath,
      });
    });
    return crumbs;
  };

  const crumbs = breadcrumbsProp || items || generateBreadcrumbs();
  const currentPage = pageTitle || crumbs[crumbs.length - 1]?.name || '';

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-4"
      style={{ borderBottom: `1px solid ${SE.hairline}` }}
    >
      {/* ── PAGE TITLE ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2.5">
        {/* Champagne accent rail */}
        <span
          className="flex-shrink-0 w-1 h-6 rounded-full"
          style={{ backgroundColor: SE.champagne }}
          aria-hidden
        />
        <h1
          className="text-[17px] font-bold tracking-tight leading-tight"
          style={{ color: SE.title }}
        >
          {currentPage}
        </h1>
      </div>

      {/* ── BREADCRUMB TRAIL ───────────────────────────────────────── */}
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1 flex-wrap"
      >
        {crumbs.map((item, index) => (
          <React.Fragment key={`${item.name}-${index}`}>
            {index > 0 && (
              <svg
                width="12" height="12" viewBox="0 0 12 12" fill="none"
                aria-hidden
                className="flex-shrink-0"
                style={{ color: SE.champagne }}
              >
                <path
                  d="M4.5 2.5L7.5 6L4.5 9.5"
                  stroke="currentColor" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            )}

            {item.path ? (
              <Link
                to={item.path}
                className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium tracking-tight transition-all duration-150"
                style={{ color: SE.muted }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.color = SE.title;
                  (e.currentTarget as HTMLElement).style.backgroundColor = SE.hoverWash;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.color = SE.muted;
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                {index === 0 && (
                  <HomeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                )}
                {item.name}
              </Link>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px] font-semibold tracking-tight"
                style={{
                  backgroundColor: SE.champagneWash,
                  color: SE.bottle,
                  border: `1px solid ${SE.champagneRing}`,
                }}
              >
                {pageTitle || item.name}
              </span>
            )}
          </React.Fragment>
        ))}
      </nav>
    </div>
  );
};

export default PageBreadcrumb;
